// Top-level chat workspace widget. Builds assistant-ui runtime instances from
// AgentHub-owned browser runtime state + REST/native stream APIs and renders the shell
// layout (sidebar + active Thread). Charts/tool-calls/HITL/composer are all
// owned by Thread now — this widget's only job is runtime wiring + shell
// composition.

import { useCallback, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, PanelRight } from 'lucide-react';
import { AssistantRuntimeProvider } from '@assistant-ui/react';
import type { ChatRuntime, PendingRunInteractionView, ThreadMessageRecordView } from '@/shared/chat-runtime';
import { AgenthubInterruptProvider } from '@/shared/chat-runtime/AgenthubInterruptProvider';
import { useNativeAgenthubExternalRuntime } from '@/shared/chat-runtime/useNativeAgenthubExternalRuntime';
import {
  ThreadList,
  useThreadSelection,
  type ThreadSelection,
} from '@/features/chat/thread-history';
import { UpdateArtifactToolUI } from '@/features/chat/artifact-panel';
import { SpecialistToolUIs } from '@/features/chat/specialist-card';
import { useCapabilities } from '@/app/providers/CapabilitiesProvider';
import { useAgentCatalog } from '@/app/providers/AgentCatalogProvider';
import { toAgentThreadSuggestions } from '@/entities/agent';
import { useArtifactPanelStore } from '@/shared/stores/artifactPanel';
import { useChatViewStore, useChatWorkspaceStore } from '@/shared/chat-workspace';
import { ApiError } from '@/shared/api';
import { Button } from '@/shared/ui/button';
import { ChatLayout } from './ChatLayout';
import { Thread } from './Thread';
import { ArtifactPanel } from './ArtifactPanel';
import { invalidatePromptedAgentIds, invalidateThreadList } from '@/entities/thread';

type ChatWorkspaceProps = {
  agentId: string;
  initialThreadId?: string | null;
  runtime: ChatRuntime;
  threadSelection?: ThreadSelection;
  hideThreadSidebar?: boolean;
};

type ChatWorkspaceRuntimeProps = {
  agentId: string;
  threadId: string | null;
  isActive: boolean;
  recoverActiveRunOnMount: boolean;
  activeRunStartedAt: number | null;
  activeRunId: string | null;
  runtime: ChatRuntime;
  persistedMessages: ThreadMessageRecordView[];
  persistedPendingInteraction: PendingRunInteractionView | null;
  onThreadAccepted: (threadId: string, runId?: string | null) => void;
  onRunFinished: (runId?: string) => void;
};

function ChatWorkspaceRuntime({
  agentId,
  threadId,
  isActive,
  recoverActiveRunOnMount,
  activeRunStartedAt,
  activeRunId,
  runtime,
  persistedMessages,
  persistedPendingInteraction,
  onThreadAccepted,
  onRunFinished,
}: ChatWorkspaceRuntimeProps) {
  const { capabilities } = useCapabilities();
  const { agents } = useAgentCatalog();
  const suggestions = useMemo(
    () => toAgentThreadSuggestions(agents.find((a) => a.id === agentId)),
    [agents, agentId],
  );

  const { assistantRuntime, interrupts, submitInterruptResponses, runExpired, restartConversation } = useNativeAgenthubExternalRuntime({
    agentId,
    threadId,
    persistedMessages,
    persistedPendingInteraction,
    api: runtime.api,
    baseUrl: runtime.baseUrl,
    streamTimeoutMs: runtime.streamTimeoutMs,
    uploadLimits: capabilities?.upload_limits,
    onThreadAccepted,
    onRunFinished,
    recoverActiveRunOnMount,
    activeRunStartedAt,
    activeRunId,
    suggestions,
  });

  // Stable identity unless the pause state itself changes, so context
  // consumers (the last message's action-bar slot, InterruptPanel) do not
  // re-render on every streaming update of this widget.
  const interruptContext = useMemo(
    () => ({ interrupts, submitInterruptResponses, runExpired, restartConversation }),
    [interrupts, submitInterruptResponses, runExpired, restartConversation],
  );

  return (
    <AssistantRuntimeProvider runtime={assistantRuntime}>
      <AgenthubInterruptProvider value={interruptContext}>
        {/* Global (not per-message) tool-call-UI registration: writes into
            useArtifactPanelStore as `generative-ui-artifact` calls stream in. */}
        <UpdateArtifactToolUI threadId={threadId ?? 'new'} />
        <SpecialistToolUIs />
        {isActive && <Thread agentId={agentId} threadId={threadId} />}
      </AgenthubInterruptProvider>
    </AssistantRuntimeProvider>
  );
}

type ChatWorkspaceRuntimeSlotProps = {
  agentId: string;
  threadId: string | null;
  isActive: boolean;
  runtime: ChatRuntime;
  selectThread: (threadId: string | null) => void;
};

function ChatWorkspaceRuntimeSlot({
  agentId,
  threadId,
  isActive,
  runtime,
  selectThread,
}: ChatWorkspaceRuntimeSlotProps) {
  const queryClient = useQueryClient();
  const messagesQuery = runtime.threadQueries.useThreadMessagesQuery(threadId);
  const persistedMessages = useMemo(
    () => [...(messagesQuery.data?.messages ?? [])],
    [messagesQuery.data],
  );

  // Delay the first mount until this slot's selected transcript has loaded.
  // The runtime state itself lives outside this component, but the first
  // assistant-ui provider still needs a stable initial transcript.
  const historyPending = threadId !== null && (messagesQuery.isLoading || messagesQuery.isPlaceholderData);
  const hasHydratedRef = useRef(false);
  const pendingAcceptedThreadRef = useRef<string | null>(null);
  // A later background refetch (react-query staleTime elapsing, window
  // focus, etc.) can race the backend clearing `activeRun` on the thread
  // record, returning one last stale snapshot where `activeRun` still looks
  // live even though this tab just finished it locally. That stale snapshot
  // flips `shouldRecoverActiveRun` back to true and re-fires the reattach
  // effect, which puts the thread back into `isRunning` with no
  // `runStartedAt` — the reported "Working…" that never clears. Once this
  // tab has locally finished a given run, ignore that same run if the query
  // ever reports it as active again.
  const dismissedRunIdRef = useRef<string | null>(null);
  const activeRun = messagesQuery.data?.activeRun ?? null;
  const recoveryRunId = useChatWorkspaceStore((store) => {
    const workspace = store.agents[agentId];
    return workspace?.activeThreadId === threadId ? workspace.recoveryRunId ?? null : null;
  });
  const recoverRunId = activeRun?.runId ?? recoveryRunId;
  // A persisted assistant message only exists once finalize_turn has fully
  // completed and written it — an in-progress/paused run has no such row yet
  // (just run_events). So a persisted message for recoverRunId is proof this
  // run's content is already in the hydrated transcript: `activeRun` here is
  // stale (e.g. served from cache within the query's staleTime window before
  // it re-validated), and replaying the run's full event history on mount
  // would duplicate content already rendered from the persisted record.
  const recoverRunAlreadyPersisted = recoverRunId !== null
    && persistedMessages.some((message) => message.role === 'assistant' && message.runId === recoverRunId);
  const shouldRecoverActiveRun = threadId !== null && recoverRunId !== null
    && recoverRunId !== dismissedRunIdRef.current
    && !recoverRunAlreadyPersisted;
  const persistedPendingInteraction = activeRun?.pendingInteraction ?? null;
  // handleThreadAccepted/handleRunFinished feed useNativeAgenthubExternalRuntime's
  // onThreadAccepted/onRunFinished, which cascade into consume/acknowledgeThread/
  // recoverStream and finally the recoverActiveRunOnMount effect's dependency
  // array. Plain inline functions here got a new identity on every render of
  // this component (query refetch, store update, etc.), which tore down and
  // restarted — or silently aborted — a live reattach stream on renders that
  // had nothing to do with the run itself. Route the actually-volatile reads
  // (activeRun, recoveryRunId, selectThread) through refs so these callbacks
  // stay referentially stable across renders, keyed only on the truly stable
  // per-slot values (this slot is keyed by threadId, so threadId/agentId never
  // change across this instance's lifetime; queryClient is stable per provider).
  const latestActiveRunRef = useRef(activeRun);
  latestActiveRunRef.current = activeRun;
  const latestRecoveryRunIdRef = useRef(recoveryRunId);
  latestRecoveryRunIdRef.current = recoveryRunId;
  const latestSelectThreadRef = useRef(selectThread);
  latestSelectThreadRef.current = selectThread;
  const handleThreadAccepted = useCallback((acceptedThreadId: string, acceptedRunId: string | null = null) => {
    void invalidateThreadList(queryClient);
    void invalidatePromptedAgentIds(queryClient);
    if (threadId === null) {
      // Preserve the accepted thread immediately so a browser refresh can
      // restore it, but defer view selection until this live runtime finishes.
      // Selecting here remounts assistant-ui and would discard current deltas.
      useChatWorkspaceStore.getState().markThreadActive(agentId, acceptedThreadId, true, acceptedRunId);
      useChatViewStore.getState().markThreadAcceptedPending(agentId, acceptedThreadId);
      pendingAcceptedThreadRef.current = acceptedThreadId;
    } else {
      useChatWorkspaceStore.getState().markThreadActive(agentId, acceptedThreadId, true, acceptedRunId);
      if (acceptedThreadId !== threadId) latestSelectThreadRef.current(acceptedThreadId);
    }
  }, [agentId, queryClient, threadId]);
  const handleRunFinished = useCallback((finishedRunId?: string) => {
    void invalidatePromptedAgentIds(queryClient);
    // Prefer the terminal event's own run_id over the query cache's `activeRun`:
    // on a new thread's first turn no query has fetched this thread yet, so
    // `activeRun` is still null here and the dismiss guard below would miss the
    // real run entirely (see the race note above).
    //
    // No DB refetch here: by the time `done`/`error` reaches this callback,
    // the locally streamed transcript in agenthubRuntimeStore already holds
    // the authoritative final (or clearly error-marked) message — see
    // useNativeAgenthubExternalRuntime's consume(), which only calls
    // onRunFinished once that terminal event has been applied. Refetching
    // just to re-hydrate it swapped in a differently-shaped DB row (client
    // message ids are locally generated UUIDs, never reconciled with the
    // server's persisted row id) and caused a visible flicker for no
    // benefit. hydrateSession's guard also refuses to overwrite a
    // populated session, so any later background refetch is inert here too.
    const completedRunId = finishedRunId ?? latestActiveRunRef.current?.runId ?? latestRecoveryRunIdRef.current;
    dismissedRunIdRef.current = completedRunId ?? null;
    if (threadId && completedRunId) {
      useChatWorkspaceStore.getState().clearRecoveryRun(agentId, threadId, completedRunId);
    }
    const acceptedThreadId = pendingAcceptedThreadRef.current;
    pendingAcceptedThreadRef.current = null;
    if (acceptedThreadId) latestSelectThreadRef.current(acceptedThreadId);
  }, [agentId, queryClient, threadId]);
  if (!historyPending) hasHydratedRef.current = true;

  if (!hasHydratedRef.current) {
    if (!isActive) return null;
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="size-6 animate-spin text-muted-foreground" aria-label="Loading conversation" />
      </div>
    );
  }

  if (messagesQuery.isError) {
    if (!isActive) return null;
    const message =
      messagesQuery.error instanceof ApiError && messagesQuery.error.context?.status === 404
        ? 'This session is no longer available. It may have been deleted in another window.'
        : 'We could not load this session. Please try again.';
    return (
      <div className="flex h-full items-center justify-center p-6">
        <p role="alert" className="max-w-md text-center text-sm text-muted-foreground">
          {message}
        </p>
      </div>
    );
  }

  return (
    <div className={isActive ? 'h-full' : 'hidden'}>
      <ChatWorkspaceRuntime
        agentId={agentId}
        threadId={threadId}
        isActive={isActive}
        recoverActiveRunOnMount={shouldRecoverActiveRun}
        activeRunStartedAt={activeRun?.startedAt ? Date.parse(activeRun.startedAt) : null}
        activeRunId={recoverRunId}
        runtime={runtime}
        persistedMessages={persistedMessages}
        persistedPendingInteraction={persistedPendingInteraction}
        onThreadAccepted={handleThreadAccepted}
        onRunFinished={handleRunFinished}
      />
    </div>
  );
}

export function ChatWorkspace({
  agentId,
  initialThreadId = null,
  runtime,
  threadSelection,
  hideThreadSidebar = false,
}: ChatWorkspaceProps) {
  const { Layout } = runtime.pageShell;
  const localSelection = useThreadSelection(initialThreadId);
  const { threadId, selectThread } = threadSelection ?? localSelection;

  // Preserve mounted providers for threads visited in this agent panel while
  // the external store migration settles. The in-flight stream state itself is
  // AgentHub-owned now; this no longer acts as the sole stream-preservation
  // mechanism.
  const runtimeThreadIdsRef = useRef<Array<string | null>>([]);
  if (!runtimeThreadIdsRef.current.includes(threadId)) {
    runtimeThreadIdsRef.current.push(threadId);
  }
  // Scopes the artifact panel to the selected thread so a brand-new,
  // not-yet-accepted thread never shows a stale panel from elsewhere.
  const artifactPanelThreadId = threadId ?? 'new';
  const isArtifactPanelOpen = useArtifactPanelStore((s) => s.panels[artifactPanelThreadId]?.isOpen ?? false);
  const openArtifactPanel = useArtifactPanelStore((s) => s.open);

  return (
    <Layout>
      <ChatLayout
        artifactPanel={<ArtifactPanel threadId={artifactPanelThreadId} />}
        artifactPanelOpen={isArtifactPanelOpen}
        sidebar={
          hideThreadSidebar ? undefined : (
            <ThreadList
              agentId={agentId}
              selectedThreadId={threadId}
              runtime={runtime}
              onSelect={selectThread}
            />
          )
        }
        content={
          <div className="relative h-full">
            {/* On-demand reopen affordance: the panel otherwise only opens
                automatically while `generative-ui-artifact` streams in, so closing
                it (or losing the live trigger on reload) leaves no way back
                in — reopens to the last revision in the store, or a graceful
                empty state if this thread never recovered one (see
                ArtifactPanel's no-revisions branch). */}
            {threadId !== null && !isArtifactPanelOpen && (
              <Button
                variant="outline"
                size="icon-sm"
                aria-label="Open artifact panel"
                title="Open artifact panel"
                className="absolute top-2 right-4 z-10"
                onClick={() => openArtifactPanel(artifactPanelThreadId)}
              >
                <PanelRight />
              </Button>
            )}
            {runtimeThreadIdsRef.current.map((runtimeThreadId) => (
              <ChatWorkspaceRuntimeSlot
                key={runtimeThreadId ?? 'new'}
                agentId={agentId}
                threadId={runtimeThreadId}
                isActive={runtimeThreadId === threadId}
                runtime={runtime}
                selectThread={selectThread}
              />
            ))}
          </div>
        }
      />
    </Layout>
  );
}
